import React, { useCallback, useEffect, useState } from 'react';
import {
  Activity, AlertTriangle, ArrowRight, Cloud, Clock, ExternalLink, Flame, Lock, LogOut, MapPin,
  RadioTower, RefreshCw, Settings, ShieldCheck, Siren, TrafficCone, Waves,
} from 'lucide-react';
import Header from '../components/Header';
import RoadworksMap from '../components/RoadworksMap';
import Footer from '../components/Footer';
import SEO from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const REFRESH_MS = 60_000;

const STATUS_STYLES = {
  'MAJOR INCIDENT': {
    pill: 'bg-red-500/15 text-red-300 ring-red-500/30',
    dot: 'bg-red-400',
    glow: 'from-red-500/20',
  },
  WATCH: {
    pill: 'bg-amber-500/15 text-amber-300 ring-amber-500/30',
    dot: 'bg-amber-400',
    glow: 'from-amber-500/20',
  },
  NORMAL: {
    pill: 'bg-emerald-500/15 text-emerald-300 ring-emerald-500/30',
    dot: 'bg-emerald-400',
    glow: 'from-emerald-500/10',
  },
};

const SEVERITY_STYLES = {
  high: { dot: 'bg-red-500', chip: 'bg-red-50 text-red-700 border-red-200', word: 'High' },
  medium: { dot: 'bg-amber-500', chip: 'bg-amber-50 text-amber-800 border-amber-200', word: 'Medium' },
  low: { dot: 'bg-slate-400', chip: 'bg-slate-50 text-slate-600 border-slate-200', word: 'Low' },
};

const TYPE_ICONS = {
  traffic: TrafficCone,
  roadworks: TrafficCone,
  weather: Cloud,
  police: Siren,
  fire: Flame,
};

const SOURCE_STATUS = {
  ok: { text: 'text-emerald-600', dot: 'bg-emerald-500', label: 'Live' },
  degraded: { text: 'text-amber-600', dot: 'bg-amber-500', label: 'Degraded' },
  down: { text: 'text-red-600', dot: 'bg-red-500', label: 'Down' },
  unknown: { text: 'text-slate-400', dot: 'bg-slate-300', label: 'Not checked' },
};

const relTime = (iso) => {
  if (!iso) return '—';
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} h ago`;
  return `${Math.floor(diff / 86400)} d ago`;
};

const absTime = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-GB', {
      day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return '—';
  }
};

const Metric = ({ label, value, sub, testId }) => (
  <div className="rounded-xl bg-white/5 ring-1 ring-white/10 px-4 py-3" data-testid={testId}>
    <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">{label}</div>
    <div className="mt-1 text-2xl font-bold text-white tabular-nums">{value}</div>
    {sub ? <div className="text-xs text-slate-400 mt-0.5">{sub}</div> : null}
  </div>
);

const IncidentCard = ({ item }) => {
  const sev = SEVERITY_STYLES[item.severity] || SEVERITY_STYLES.low;
  const Icon = TYPE_ICONS[item.type] || AlertTriangle;
  const isUpdate = item.bucket === 'update';
  const isRoadworks = item.bucket === 'roadworks';
  return (
    <article
      className="group relative rounded-xl bg-white border border-slate-200 p-5 transition-shadow duration-200 hover:shadow-[0_8px_28px_rgba(15,23,42,0.08)]"
      data-testid={`incident-card-${item.id}`}
    >
      <span
        className={`absolute left-0 top-5 bottom-5 w-[3px] rounded-full ${isUpdate ? 'bg-slate-300' : sev.dot}`}
        aria-hidden
      />
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <span className="mt-0.5 shrink-0 rounded-lg bg-slate-100 p-2 text-slate-600">
            <Icon className="w-4 h-4" />
          </span>
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-slate-900 leading-snug" data-testid={`incident-label-${item.id}`}>
              {item.title || item.label}
            </h3>
            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
              <span className="inline-flex items-center gap-1">
                <MapPin className="w-3 h-3" />{item.area_label}
              </span>
              {!isUpdate && (
                <span className={`inline-flex items-center rounded border px-1.5 py-0.5 font-semibold ${sev.chip}`}>
                  {sev.word} severity
                </span>
              )}
              {item.bucket === 'live' && (
                <span className="inline-flex items-center gap-1 rounded border border-sky-200 bg-sky-50 px-1.5 py-0.5 font-semibold text-sky-700">
                  <span className="w-1.5 h-1.5 rounded-full bg-sky-500 animate-pulse" />Ongoing
                </span>
              )}
              {isRoadworks && (
                <span className="inline-flex items-center rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 font-semibold text-slate-600">
                  Planned works
                </span>
              )}
              {item.bucket === 'resolved' && (
                <span className="inline-flex items-center rounded border border-emerald-200 bg-emerald-50 px-1.5 py-0.5 font-semibold text-emerald-700">
                  Resolved
                </span>
              )}
              {isUpdate && (
                <span className="inline-flex items-center rounded border border-slate-200 bg-white px-1.5 py-0.5 text-slate-500">
                  Official statement{item.period_label ? ` · ${item.period_label}` : ''}
                </span>
              )}
            </div>
          </div>
        </div>
        <Lock className={`w-3.5 h-3.5 shrink-0 ${item.locked ? 'text-slate-300' : 'text-emerald-500'}`} title={item.locked ? 'Full details for subscribers' : 'Subscriber view'} />
      </div>

      {item.locked === false && (
        <div className="mt-3 space-y-2">
          {item.subscriber_summary ? (
            <p className="text-sm text-slate-700 leading-relaxed" data-testid={`incident-subscriber-summary-${item.id}`}>
              {item.subscriber_summary}
            </p>
          ) : null}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
            {item.road ? <span>Road: <span className="font-medium text-slate-700">{item.road}</span></span> : null}
            {item.area ? <span>Location: <span className="font-medium text-slate-700">{item.area}</span></span> : null}
            {item.source_name ? <span>Source: <span className="font-medium text-slate-700">{item.source_name}</span></span> : null}
            {item.source_url ? (
              <a
                href={item.source_url}
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="inline-flex items-center gap-1 font-semibold text-sky-600 hover:text-sky-700"
                data-testid={`incident-source-link-${item.id}`}
              >
                Open official source<ExternalLink className="w-3 h-3" />
              </a>
            ) : null}
          </div>
        </div>
      )}

      {item.withheld ? (
        <p className="mt-3 text-sm text-slate-500 italic" data-testid={`incident-withheld-${item.id}`}>
          Just detected — subscribers have this now. A general summary appears here shortly.
        </p>
      ) : item.summary ? (
        <p className="mt-3 text-sm text-slate-600 leading-relaxed" data-testid={`incident-summary-${item.id}`}>
          {item.summary}
          {item.information_state === 'developing' ? (
            <span className="ml-1.5 inline-flex items-center rounded bg-amber-50 border border-amber-200 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700">
              Developing
            </span>
          ) : null}
        </p>
      ) : null}

      <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-1 border-t border-slate-100 pt-3 text-xs text-slate-400">
        <span>
          {isUpdate ? 'Picked up' : 'First detected'}{' '}
          <span className="text-slate-600 font-medium">{absTime(item.first_detected_at)}</span>
        </span>
        <span>Last updated <span className="text-slate-600 font-medium">{relTime(item.last_updated_at)}</span></span>
      </div>
    </article>
  );
};

const SectionHeading = ({ title, subtitle, testId, children }) => (
  <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-2" data-testid={testId}>
    <div className="min-w-0">
      <h2 className="min-w-0 text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">{title}</h2>
      {subtitle ? <p className="mt-2 text-sm text-slate-500">{subtitle}</p> : null}
    </div>
    {children}
  </div>
);

const IncidentWatchPage = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [account, setAccount] = useState(null);
  const [premium, setPremium] = useState(null);
  const [busy, setBusy] = useState('');
  const [notice, setNotice] = useState('');

  const loadAccount = useCallback(async () => {
    try {
      const res = await fetch(`${API}/subscriber/auth/me`, { credentials: 'include' });
      if (!res.ok) { setAccount(null); setPremium(null); return null; }
      const me = await res.json();
      setAccount(me);
      if (me.entitlement?.active) {
        const feed = await fetch(`${API}/incident-watch/feed?status=all&limit=100`, { credentials: 'include' });
        if (feed.ok) setPremium(await feed.json());
      } else {
        setPremium(null);
      }
      return me;
    } catch {
      setAccount(null);
      return null;
    }
  }, []);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API}/incident-watch/public/status`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setData(await res.json());
      setError('');
    } catch (e) {
      setError('Could not reach Incident Watch. Retrying shortly.');
    } finally {
      setLoading(false);
    }
  }, []);

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const signIn = () => {
    const redirectUrl = window.location.origin + '/incident-watch';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const signOut = async () => {
    await fetch(`${API}/subscriber/auth/logout`, { method: 'POST', credentials: 'include' });
    setAccount(null);
    setPremium(null);
  };

  const subscribe = async (lookupKey) => {
    if (!account) { signIn(); return; }
    setBusy(lookupKey);
    try {
      const res = await fetch(`${API}/iw/billing/checkout`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ lookup_key: lookupKey, origin_url: window.location.origin }),
      });
      const body = await res.json();
      if (!res.ok || !body.checkout_url) throw new Error(body.detail || 'Checkout failed');
      window.location.href = body.checkout_url;
    } catch (e) {
      setNotice('Could not start checkout. Please try again.');
      setBusy('');
    }
  };

  const manage = async () => {
    setBusy('portal');
    try {
      const res = await fetch(`${API}/iw/billing/portal`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ lookup_key: 'incident_watch_monthly', origin_url: window.location.origin }),
      });
      const body = await res.json();
      if (!res.ok || !body.portal_url) throw new Error('portal failed');
      window.location.href = body.portal_url;
    } catch {
      setNotice('Could not open the billing portal.');
    } finally {
      setBusy('');
    }
  };

  // Emergent auth callback: exchange the one-time session_id, then clean the URL.
  useEffect(() => {
    const run = async () => {
      const hash = window.location.hash || '';
      if (hash.includes('session_id=')) {
        const sessionId = new URLSearchParams(hash.replace(/^#/, '')).get('session_id');
        if (sessionId) {
          try {
            await fetch(`${API}/subscriber/auth/session`, {
              method: 'POST',
              credentials: 'include',
              headers: { 'Content-Type': 'application/json', 'X-Session-ID': sessionId },
              body: JSON.stringify({ session_id: sessionId }),
            });
          } catch { /* surfaced by the account load below */ }
        }
        window.history.replaceState({}, '', '/incident-watch');
      }

      const params = new URLSearchParams(window.location.search);
      if (params.get('checkout') === 'success' && params.get('session_id')) {
        setNotice('Confirming your subscription with Stripe…');
        const sid = params.get('session_id');
        for (let i = 0; i < 12; i += 1) {
          const r = await fetch(`${API}/iw/billing/status/${sid}`);
          const s = r.ok ? await r.json() : {};
          if (s.payment_status === 'paid') break;
          await new Promise((done) => setTimeout(done, 1500));
        }
        setNotice('Subscription active — welcome to Incident Watch.');
        window.history.replaceState({}, '', '/incident-watch');
      } else if (params.get('checkout') === 'cancelled') {
        setNotice('Checkout cancelled — nothing has been charged.');
        window.history.replaceState({}, '', '/incident-watch');
      }

      await loadAccount();
      await load();
    };
    run();
    const t = setInterval(() => { load(); }, REFRESH_MS);
    return () => clearInterval(t);
  }, [load, loadAccount]);

  const status = data?.status_code || 'NORMAL';
  const style = STATUS_STYLES[status] || STATUS_STYLES.NORMAL;
  const entitled = !!account?.entitlement?.active;
  const premiumItems = premium?.incidents || null;
  const usePremium = entitled && !!premiumItems;
  const RESOLVED_WINDOW_MS = 12 * 60 * 60 * 1000;
  const live = usePremium
    ? premiumItems.filter((i) => i.bucket === 'live' && i.status === 'active')
    : (data?.live || []);
  const roadworks = usePremium
    ? premiumItems.filter((i) => i.bucket === 'roadworks' && i.status === 'active').slice(0, 12)
    : (data?.roadworks || []);
  const updates = usePremium
    ? premiumItems.filter((i) => i.bucket === 'update' && i.status === 'active')
    : (data?.updates || []);
  const resolved = usePremium
    ? premiumItems.filter((i) => i.bucket === 'resolved' && i.resolved_at
        && Date.now() - new Date(i.resolved_at).getTime() < RESOLVED_WINDOW_MS)
    : (data?.resolved || []);
  const degraded = data?.feeds_degraded || [];

  return (
    <div className="min-h-screen bg-slate-50">
      <SEO
        title="Paisley Incident Watch — live local incident monitoring"
        description="Live monitoring of road incidents, weather warnings, police and fire service statements across Paisley and Renfrewshire. Free town status, full detail for subscribers."
        url="/incident-watch"
      />
      <Header />

      <section className="relative overflow-hidden bg-slate-900" data-testid="iw-hero">
        <div className={`pointer-events-none absolute inset-0 bg-gradient-to-br ${style.glow} via-transparent to-transparent`} />
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.06]"
          style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,.7) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.7) 1px, transparent 1px)', backgroundSize: '46px 46px' }}
        />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-12">
          <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-sky-300/90">
            <RadioTower className="w-3.5 h-3.5" />
            Renfrewshire monitoring
          </div>
          <h1 className="mt-3 text-4xl sm:text-5xl font-bold tracking-tight text-white">
            Paisley Incident Watch
          </h1>
          <p className="mt-3 max-w-2xl text-base text-slate-300">
            Official sources, polled every few minutes. No rumour, no speculation — only what the
            source has actually published.
          </p>

          <div className="mt-7 flex flex-wrap items-center gap-3">
            <div
              className={`inline-flex items-center gap-2.5 rounded-full px-4 py-2 ring-1 ${style.pill}`}
              data-testid="iw-status-pill"
            >
              <span className={`w-2 h-2 rounded-full ${style.dot} ${status !== 'NORMAL' ? 'animate-pulse' : ''}`} />
              <span className="text-sm font-bold tracking-wide">{status}</span>
              <span className="text-xs opacity-80">· {data?.status_label || 'Checking…'}</span>
            </div>
            <div
              className="inline-flex items-center gap-2 rounded-full bg-white/5 px-3 py-2 ring-1 ring-white/10"
              data-testid="iw-live-indicator"
            >
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
              </span>
              <span className="text-[10px] font-bold uppercase tracking-widest text-emerald-300">Live</span>
              <span className="text-xs text-slate-400">
                engine checked {relTime(data?.last_successful_check)}
              </span>
            </div>
          </div>

          <div className="mt-8 grid grid-cols-2 lg:grid-cols-5 gap-3">
            <Metric
              label="Live incidents"
              value={loading ? '—' : data?.live_count ?? 0}
              sub="Happening now"
              testId="iw-metric-active"
            />
            <Metric
              label="Roadworks"
              value={loading ? '—' : data?.roadworks_count ?? 0}
              sub={`${data?.roadworks_closures ?? 0} major closures`}
              testId="iw-metric-roadworks"
            />
            <Metric
              label="Official updates"
              value={loading ? '—' : data?.updates_count ?? 0}
              sub="Statements, not live"
              testId="iw-metric-updates"
            />
            <Metric
              label="Last check"
              value={loading ? '—' : relTime(data?.last_successful_check)}
              sub={absTime(data?.last_successful_check)}
              testId="iw-metric-lastcheck"
            />
            <Metric
              label="Sources"
              value={loading ? '—' : `${(data?.sources || []).filter((s) => s.status === 'ok').length}/${(data?.sources || []).length}`}
              sub={degraded.length ? `${degraded.length} degraded` : 'All reporting'}
              testId="iw-metric-sources"
            />
          </div>
        </div>
      </section>

      {(data?.trunk_alerts || []).length > 0 && (
        <div className="bg-slate-900 border-y border-sky-900/40" data-testid="iw-trunk-banner">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-start gap-3 min-w-0">
                <span className="mt-0.5 shrink-0 rounded-lg bg-sky-500/15 p-2 text-sky-300 ring-1 ring-sky-500/30">
                  <TrafficCone className="w-4 h-4" />
                </span>
                <div className="min-w-0">
                  <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-sky-300/90">
                    Trunk road disruption
                  </p>
                  <ul className="mt-1 space-y-0.5">
                    {data.trunk_alerts.map((t) => (
                      <li key={t.id} className="text-sm font-semibold text-white leading-snug" data-testid={`trunk-alert-${t.id}`}>
                        {t.label}
                      </li>
                    ))}
                  </ul>
                  <p className="mt-1.5 text-xs text-slate-400">
                    M-roads and A-road trunk routes carry the most journeys, so these are flagged separately.
                  </p>
                </div>
              </div>
              <a
                href="#roadworks"
                onClick={(e) => {
                  e.preventDefault();
                  document.querySelector('[data-testid="iw-roadworks-section"]')?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="shrink-0 inline-flex items-center gap-1.5 self-start rounded-full bg-white/10 px-4 py-2 text-xs font-semibold text-white ring-1 ring-white/15 transition-colors duration-200 hover:bg-white/20"
                data-testid="iw-trunk-banner-cta"
              >
                See all works
              </a>
            </div>
          </div>
        </div>
      )}

      {degraded.length > 0 && (
        <div className="bg-amber-50 border-y border-amber-200" data-testid="iw-degraded-banner">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-2.5 flex items-center gap-2 text-sm text-amber-900">
            <AlertTriangle className="w-4 h-4 shrink-0" />
            <span>
              <strong>Reduced coverage:</strong> {degraded.join(', ')} not reporting. Other sources unaffected.
            </span>
          </div>
        </div>
      )}

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {notice && (
          <div className="mb-6 rounded-xl border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-900" data-testid="iw-notice">
            {notice}
          </div>
        )}
        {error && (
          <div className="mb-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" data-testid="iw-error">
            {error}
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 min-w-0">
            <SectionHeading
              testId="iw-live-section"
              title={
                live.length > 0
                  ? `${live.length} live ${live.length === 1 ? 'incident' : 'incidents'} around Paisley`
                  : 'Live incidents'
              }
              subtitle={entitled
                ? 'Subscriber view — exact locations, source links and live updates, no delay.'
                : 'Free view shows the type, approximate area and severity. Exact locations, live updates and source links are for subscribers.'}
            >
              <button
                onClick={load}
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-sky-600 transition-colors"
                data-testid="iw-refresh-btn"
              >
                <RefreshCw className="w-3.5 h-3.5" />Refresh
              </button>
            </SectionHeading>

            <div className="mt-6 space-y-3" data-testid="iw-incident-list">
              {loading && (
                <div className="rounded-xl border border-slate-200 bg-white p-8 text-center text-sm text-slate-400">
                  Checking sources…
                </div>
              )}
              {!loading && live.length === 0 && (
                <div
                  className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-8 text-center"
                  data-testid="iw-no-live-incidents"
                >
                  <ShieldCheck className="w-8 h-8 mx-auto text-emerald-600" />
                  <h3 className="mt-3 text-xl font-bold tracking-tight text-slate-900">
                    No live incidents currently detected
                  </h3>
                  <p className="mt-2 text-sm text-slate-600 max-w-md mx-auto">
                    Nothing is currently disrupting Paisley or Renfrewshire on any monitored live
                    feed. Recent official statements are listed below.
                  </p>
                </div>
              )}
              {live.map((item) => <IncidentCard key={item.id} item={item} />)}
            </div>

            {roadworks.length > 0 && (
              <section className="mt-12" data-testid="iw-roadworks-section">
                <h2 className="text-xl sm:text-2xl font-bold tracking-tight text-slate-900">
                  Roadworks &amp; street closures
                </h2>
                <p className="mt-2 text-sm text-slate-500">
                  {data?.roadworks_count ?? roadworks.length} registered works under way across Renfrewshire
                  {data?.roadworks_closures ? `, including ${data.roadworks_closures} major trunk-road or long-term closures` : ''}.
                  Planned disruption from the Scottish Road Works Register — counted separately from live incidents.
                </p>
                <div className="mt-6" data-testid="iw-roadworks-map-wrap">
                  <RoadworksMap points={data?.roadworks_map || []} />
                </div>
                <div className="mt-6 space-y-3" data-testid="iw-roadworks-list">
                  {roadworks.map((item) => <IncidentCard key={item.id} item={item} />)}
                </div>
                {(data?.roadworks_count ?? 0) > roadworks.length ? (
                  <p className="mt-4 text-xs text-slate-400">
                    Showing the {roadworks.length} most disruptive of {data.roadworks_count}.
                  </p>
                ) : null}
              </section>
            )}

            {updates.length > 0 && (
              <section className="mt-12" data-testid="iw-updates-section">
                <h2 className="text-xl sm:text-2xl font-bold tracking-tight text-slate-900">
                  Recent official updates
                </h2>
                <p className="mt-2 text-sm text-slate-500">
                  Statements from Police Scotland and the fire service. Newsworthy, but not evidence
                  of a live incident — these never count towards the live total.
                </p>
                <div className="mt-6 space-y-3" data-testid="iw-updates-list">
                  {updates.map((item) => <IncidentCard key={item.id} item={item} />)}
                </div>
              </section>
            )}

            {resolved.length > 0 && (
              <section className="mt-12" data-testid="iw-resolved-section">
                <h2 className="text-xl sm:text-2xl font-bold tracking-tight text-slate-900">
                  Recently resolved
                </h2>
                <p className="mt-2 text-sm text-slate-500">
                  Live incidents that have now ended, from the last 12 hours.
                </p>
                <div className="mt-6 space-y-3" data-testid="iw-resolved-list">
                  {resolved.map((item) => <IncidentCard key={item.id} item={item} />)}
                </div>
              </section>
            )}
          </div>

          <aside className="space-y-6 min-w-0">
            {entitled ? (
              <div
                className="rounded-2xl bg-slate-900 p-6 text-white shadow-[0_12px_40px_rgba(15,23,42,0.18)]"
                data-testid="iw-subscriber-card"
              >
                <div className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/15 px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest text-emerald-300 ring-1 ring-emerald-500/30">
                  <ShieldCheck className="w-3 h-3" />Subscriber
                </div>
                <h3 className="mt-4 text-xl font-bold tracking-tight">Full access active</h3>
                <p className="mt-2 text-sm text-slate-300 leading-relaxed">
                  Signed in as {account?.user?.email}. You are seeing exact locations, source links
                  and live updates with no delay.
                </p>
                <dl className="mt-4 space-y-1.5 text-sm text-slate-300">
                  <div className="flex justify-between gap-3">
                    <dt className="text-slate-400">Plan</dt>
                    <dd className="font-medium">
                      {account?.entitlement?.plan === 'incident_watch_annual' ? '£49/year' : '£4.99/month'}
                    </dd>
                  </div>
                  <div className="flex justify-between gap-3">
                    <dt className="text-slate-400">Status</dt>
                    <dd className="font-medium capitalize">{account?.entitlement?.status}</dd>
                  </div>
                  {account?.entitlement?.current_period_end ? (
                    <div className="flex justify-between gap-3">
                      <dt className="text-slate-400">
                        {account?.entitlement?.cancel_at_period_end ? 'Access until' : 'Renews'}
                      </dt>
                      <dd className="font-medium">{absTime(account.entitlement.current_period_end)}</dd>
                    </div>
                  ) : null}
                </dl>
                {account?.entitlement?.cancel_at_period_end ? (
                  <p className="mt-3 rounded-lg bg-amber-500/10 px-3 py-2 text-xs text-amber-200 ring-1 ring-amber-500/20">
                    Cancelled — you keep full access until the end of the paid period.
                  </p>
                ) : null}
                <button
                  onClick={manage}
                  disabled={busy === 'portal'}
                  className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-lg bg-white/10 px-4 py-2.5 text-sm font-semibold text-white ring-1 ring-white/15 transition-colors duration-200 hover:bg-white/15 disabled:opacity-50"
                  data-testid="iw-manage-subscription-btn"
                >
                  <Settings className="w-4 h-4" />
                  {busy === 'portal' ? 'Opening…' : 'Manage subscription'}
                </button>
                <button
                  onClick={signOut}
                  className="mt-2 w-full inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2 text-xs font-semibold text-slate-400 transition-colors duration-200 hover:text-white"
                  data-testid="iw-signout-btn"
                >
                  <LogOut className="w-3.5 h-3.5" />Sign out
                </button>
              </div>
            ) : (
              <div
                className="rounded-2xl bg-slate-900 p-6 text-white shadow-[0_12px_40px_rgba(15,23,42,0.18)]"
                data-testid="iw-unlock-card"
              >
                <div className="inline-flex items-center gap-1.5 rounded-full bg-sky-500/15 px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest text-sky-300 ring-1 ring-sky-500/30">
                  <Lock className="w-3 h-3" />Subscriber access
                </div>
                <h3 className="mt-4 text-xl font-bold tracking-tight">Unlock Incident Watch</h3>
                <p className="mt-2 text-sm text-slate-300 leading-relaxed">
                  Get full locations, live updates, source information and major incident alerts.
                </p>
                <ul className="mt-4 space-y-2 text-sm text-slate-300">
                  {[
                    'Exact locations and roads affected',
                    'Live updates as each incident changes',
                    'Direct links to the official source',
                    'Email alerts for major incidents',
                    'No 60-minute delay',
                  ].map((line) => (
                    <li key={line} className="flex items-start gap-2">
                      <span className="mt-1.5 w-1 h-1 rounded-full bg-sky-400 shrink-0" />
                      {line}
                    </li>
                  ))}
                </ul>
                <div className="mt-5 flex items-baseline gap-1.5">
                  <span className="text-3xl font-bold">£4.99</span>
                  <span className="text-sm text-slate-400">/month</span>
                </div>
                <p className="text-xs text-slate-400">or £49/year — two months free</p>
                {account?.entitlement && !account.entitlement.active && account.entitlement.status ? (
                  <p className="mt-3 rounded-lg bg-amber-500/10 px-3 py-2 text-xs text-amber-200 ring-1 ring-amber-500/20" data-testid="iw-lapsed-notice">
                    Your subscription is {account.entitlement.status}. Resubscribe to restore access.
                  </p>
                ) : null}
                <button
                  onClick={() => subscribe('incident_watch_monthly')}
                  disabled={busy === 'incident_watch_monthly'}
                  className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-lg bg-sky-500 px-4 py-2.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-sky-400 disabled:opacity-50"
                  data-testid="iw-subscribe-monthly-btn"
                >
                  {busy === 'incident_watch_monthly'
                    ? 'Opening Stripe…'
                    : (account?.entitlement?.status ? 'Subscribe monthly' : 'Start 7 days free')}
                  <ArrowRight className="w-4 h-4" />
                </button>
                <button
                  onClick={() => subscribe('incident_watch_annual')}
                  disabled={busy === 'incident_watch_annual'}
                  className="mt-2 w-full rounded-lg bg-white/10 px-4 py-2.5 text-sm font-semibold text-white ring-1 ring-white/15 transition-colors duration-200 hover:bg-white/15 disabled:opacity-50"
                  data-testid="iw-subscribe-annual-btn"
                >
                  {busy === 'incident_watch_annual' ? 'Opening Stripe…' : 'Get a year for £49'}
                </button>
                {account ? (
                  <button
                    onClick={signOut}
                    className="mt-3 w-full text-center text-[11px] text-slate-500 hover:text-slate-300"
                    data-testid="iw-signout-btn"
                  >
                    Signed in as {account.user.email} · Sign out
                  </button>
                ) : (
                  <button
                    onClick={signIn}
                    className="mt-3 w-full text-center text-[11px] text-slate-400 hover:text-white"
                    data-testid="iw-signin-btn"
                  >
                    Already a subscriber? Sign in
                  </button>
                )}
                <p className="mt-3 text-center text-[11px] text-slate-500">
                  {account?.entitlement?.status
                    ? 'Secure payment by Stripe · cancel anytime'
                    : '7 days free, then £4.99 a month · cancel anytime'}
                </p>
              </div>
            )}

            <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="iw-source-health">
              <div className="flex items-center gap-2">
                <Activity className="w-4 h-4 text-slate-400" />
                <h3 className="text-xs font-semibold uppercase tracking-widest text-slate-500">
                  Source health
                </h3>
              </div>
              <ul className="mt-3 space-y-2.5">
                {(data?.sources || []).map((s) => {
                  const meta = SOURCE_STATUS[s.status] || SOURCE_STATUS.unknown;
                  return (
                    <li key={s.name} className="flex items-start justify-between gap-3 text-xs" data-testid={`iw-source-${s.kind}`}>
                      <div className="min-w-0">
                        <div className="truncate font-medium text-slate-700">{s.name}</div>
                        <div className="text-slate-400">
                          {s.timeliness === 'statement' ? 'Official statements' : 'Live feed'} · checked {relTime(s.last_ok_at)}
                        </div>
                      </div>
                      <span className={`inline-flex shrink-0 items-center gap-1.5 font-semibold ${meta.text}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />{meta.label}
                      </span>
                    </li>
                  );
                })}
              </ul>
              <p className="mt-4 border-t border-slate-100 pt-3 text-[11px] leading-relaxed text-slate-400">
                If a source stops reporting it is marked here rather than quietly showing old data.
              </p>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-5">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-slate-400" />
                <h3 className="text-xs font-semibold uppercase tracking-widest text-slate-500">
                  How this works
                </h3>
              </div>
              <p className="mt-3 text-xs leading-relaxed text-slate-500">
                Sources are polled every few minutes. A <strong>live incident</strong> is something
                currently disrupting Paisley or Renfrewshire. An <strong>official update</strong> is
                a police or fire service statement — newsworthy, but never counted as live. Summaries
                are written from the published material only: never an invented cause, casualty or
                outcome, and nobody is named unless the source has already named them publicly. Live
                incidents reach the free view {data?.teaser_delay_minutes ?? 60} minutes after
                detection.
              </p>
              <div className="mt-3 flex items-center gap-1.5 text-[11px] text-slate-400">
                <Waves className="w-3 h-3" />This page refreshes every minute
              </div>
            </div>
          </aside>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default IncidentWatchPage;
